import i18nService, { ContentLanguagesDefinition } from 'common/services/i18nService';
import { ContentTypes } from 'common/store/common/constants';
import { isQuestionMode } from 'common/util/shared/sharedUtils';
import { ItemContext, QTIContextItem } from './types';

export const getEditedQuestionId = (
    currentQtiContextItem: QTIContextItem | undefined,
    contextId: number | undefined
): number | undefined => (
    currentQtiContextItem?.objectId || (isQuestionMode() ? contextId : undefined)
);

export const getQuestionSetLanguages = (itemContext?: ItemContext): string[] => (
    itemContext?.contentType === ContentTypes.QUESTION_SET
        ? (itemContext.supportedLanguages || []) as string[]
        : []
);

// Inside a question set a question is only shown in the set's languages, while the persisted
// per-question language may come from another set or from standalone editing of the question.
export const isAllowedInQuestionSet = (language: string, questionSetLanguages: string[]): boolean => (
    questionSetLanguages.length === 0 || questionSetLanguages.some((questionSetLanguage) => (
        i18nService.normalizeToContentLanguage(questionSetLanguage) ===
        i18nService.normalizeToContentLanguage(language)
    ))
);

export const clearPersistedContentLanguage = (contentItemId?: number): void => {
    if (!contentItemId) return;
    try {
        sessionStorage.removeItem(`activeQuestionLanguageFor_${contentItemId}`);
    } catch {
        console.warn('Could not clear active content language');
    }
};

export const getPersistedContentLanguage = (
    contentItemId?: number,
    supportedLanguages?: string[]
): ContentLanguagesDefinition | undefined => {
    if (!contentItemId) return undefined;
    try {
        const sessionStored = sessionStorage.getItem(`activeQuestionLanguageFor_${contentItemId}`);
        if (sessionStored) {
            const normalizedLanguage = i18nService.normalizeToContentLanguage(sessionStored);
            const supportedLower = new Set((supportedLanguages || [])
                .map((language) => i18nService.normalizeToContentLanguage(language)));
            if (supportedLanguages === undefined || supportedLanguages.length === 0 || supportedLower.has(normalizedLanguage)) {
                return normalizedLanguage;
            }
        }
        return undefined;
    } catch {
        return undefined;
    }
};

export const getPersistedQuestionContentLanguage = (
    contentItemId: number | undefined,
    questionSetLanguages: string[],
    supportedLanguages: string[],
    itemChanged: boolean
): ContentLanguagesDefinition | undefined => {
    if (!itemChanged) return undefined;

    const persistedLanguage = getPersistedContentLanguage(contentItemId);
    if (!persistedLanguage) return undefined;

    if (!isAllowedInQuestionSet(persistedLanguage, questionSetLanguages)) {
        clearPersistedContentLanguage(contentItemId);
        return undefined;
    }

    if (supportedLanguages.length > 0) {
        const normalizedSupportedLanguages = new Set(supportedLanguages.map(
            (language) => i18nService.normalizeToContentLanguage(language)
        ));
        if (!normalizedSupportedLanguages.has(i18nService.normalizeToContentLanguage(persistedLanguage))) {
            clearPersistedContentLanguage(contentItemId);
            return undefined;
        }
    }
    persistContentLanguage(persistedLanguage, contentItemId);
    return persistedLanguage;
};

export const persistContentLanguage = (lang:
    ContentLanguagesDefinition, contentItemId?: number): void => {
    if (!contentItemId) return;
    try {
        sessionStorage.setItem(
            `activeQuestionLanguageFor_${contentItemId}`,
            i18nService.normalizeToContentLanguage(lang)
        );
    } catch {
        console.warn('Could not persist active content language');
    }
};

export const getActiveContentLanguage = (
    currentActive: ContentLanguagesDefinition | undefined,
    persisted: ContentLanguagesDefinition | undefined,
    defaultLanguage: string | undefined,
    supportedLanguages: string[],
    questionSetActiveLanguage?: ContentLanguagesDefinition
): ContentLanguagesDefinition | undefined => {
    if (supportedLanguages.length === 0) {
        return undefined;
    }

    const lowerLangs = new Set(supportedLanguages.map((language) =>
        i18nService.normalizeToContentLanguage(language)));
    const normalizedCurrent = currentActive &&
        i18nService.normalizeToContentLanguage(currentActive);
    const normalizedQuestionSetActive = questionSetActiveLanguage &&
        i18nService.normalizeToContentLanguage(questionSetActiveLanguage);

    if (normalizedCurrent && lowerLangs.has(normalizedCurrent)) {
        return normalizedCurrent;
    }

    const fallbackLanguage = [persisted, defaultLanguage]
        .find((language) => language && lowerLangs.has(
            i18nService.normalizeToContentLanguage(language)
        ));

    if (fallbackLanguage) {
        return i18nService.normalizeToContentLanguage(fallbackLanguage);
    }
    // Only follow the question-set language when the question supports it,
    // otherwise no tab would be selected and the iframe would not get it.
    if (normalizedQuestionSetActive && lowerLangs.has(normalizedQuestionSetActive)) {
        return normalizedQuestionSetActive;
    }

    return i18nService.normalizeToContentLanguage(supportedLanguages[0]);
};
